import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box, Dialog, IconButton, Typography, TextField,
  CircularProgress, Tooltip, Avatar, Menu, MenuItem, Divider,
  Button, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import API_URL from '../config';
import actsLogo from '../assets/acts_logo.png';

const AI_BASE = `${API_URL}/ai-workspace`;

// Email read-only consent is persisted on the [user] table via the local API.
// Endpoints: GET/POST {API_URL}/email-access-consent
async function fetchEmailConsent() {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'GET',
    url: `${API_URL}/email-access-consent`,
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000,
  });
  // null = undecided, true = granted, false = declined
  return r.data && r.data.email_access_consent;
}

async function saveEmailConsent(value) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'POST',
    url: `${API_URL}/email-access-consent`,
    data: { email_access_consent: value },
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    timeout: 30000,
  });
  return r.data && r.data.email_access_consent;
}

// PM design tokens (match the ProjectManager visual identity).
const PM = {
  primary: '#1e3c72',
  primaryDark: '#152b53',
  gradient: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
  bg: '#f4f6f8',
  border: '#e2e8f0',
  muted: '#64748b',
  faint: '#94a3b8',
  dark: '#1e293b',
};

// Extract the assistant text from any common response shape returned by the
// new ACTS AI /chat endpoint.
function parseChatResponse(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;

  let text = '';
  if (typeof data.answer === 'string') text = data.answer;
  else if (typeof data.response === 'string') text = data.response;
  else if (typeof data.reply === 'string') text = data.reply;
  else if (typeof data.message === 'string') text = data.message;
  else if (typeof data.result === 'string') text = data.result;
  else if (typeof data.content === 'string') text = data.content;
  else if (typeof data.answer === 'object' && data.answer && typeof data.answer.text === 'string') text = data.answer.text;
  else if (typeof data.output === 'string') text = data.output;
  else if (Array.isArray(data.messages) && data.messages.length) {
    const last = [...data.messages].reverse().find((m) => m && (m.role === 'assistant' || m.role === 'ai') && typeof m.content === 'string');
    if (last) text = last.content;
  }

  return text;
}

// Split a cell value that looks like "Column|Value" style data, or just return
// the raw text. (Kept simple; the real work is detecting markdown tables.)
function isTableSeparator(line) {
  return /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');
}

function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

// Parse a message into blocks: markdown tables, blockquotes ("> ..."), or plain
// text paragraphs.
function parseBlocks(text) {
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Detect a markdown table: current line is a header with pipes, next line is
    // a separator (---), and following lines are data rows with pipes.
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    // Detect a blockquote: one or more consecutive lines starting with '>'.
    if (/^\s*>/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
      continue;
    }

    // Accumulate consecutive non-table, non-quote lines into a text block.
    const textLines = [];
    while (i < lines.length) {
      const l = lines[i];
      const nextIsTable = l.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]);
      const nextIsQuote = /^\s*>/.test(l);
      if (nextIsTable || nextIsQuote) break;
      textLines.push(l);
      i++;
    }
    if (textLines.some((l) => l.trim() !== '')) {
      blocks.push({ type: 'text', content: textLines.join('\n') });
    } else {
      i++;
    }
  }
  return blocks;
}

function MessageContent({ content }) {
  const blocks = parseBlocks(content || '');
  return (
    <Box>
      {blocks.map((b, idx) => {
        if (b.type === 'table') {
          return (
            <TableContainer key={idx} component={Paper} variant="outlined"
              sx={{ my: 1, maxWidth: '100%', overflowX: 'auto', borderRadius: 2 }}>
              <Table size="small" sx={{ minWidth: 200 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(30,60,114,0.06)' }}>
                    {(b.header || []).map((h, hi) => (
                      <TableCell key={hi} sx={{ fontWeight: 800, color: PM.dark, whiteSpace: 'nowrap' }}>
                        {renderTextWithLinks(h)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {b.rows.map((r, ri) => (
                    <TableRow key={ri}>
                      {(b.header || []).map((_, ci) => (
                        <TableCell key={ci} sx={{ color: PM.dark, whiteSpace: 'nowrap' }}>
                          {renderTextWithLinks(r[ci] ?? '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }
        if (b.type === 'blockquote') {
          return (
            <Box key={idx} sx={{
              my: 1, pl: 1.5, py: 0.75, pr: 1,
              borderLeft: '4px solid #cbd5e1',
              bgcolor: '#f8fafc',
              borderRadius: '0 8px 8px 0',
            }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: PM.muted, fontStyle: 'italic' }}>
                {renderTextWithLinks(b.content)}
              </Typography>
            </Box>
          );
        }
        return (
          <Typography key={idx} variant="body2" sx={{ whiteSpace: 'pre-wrap', color: PM.dark }}>
            {renderTextWithLinks(b.content)}
          </Typography>
        );
      })}
    </Box>
  );
}

// Turn bare URLs (http/https) and email addresses into clickable links while
// preserving the surrounding text. URLs are shown as a friendly "Open link"
// label rather than the raw address. Also renders inline **bold** and *italic*.
function renderTextWithLinks(text) {
  if (!text) return text;
  const urlRe = /(https?:\/\/[^\s<>"')\]]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g;
  const parts = String(text).split(urlRe);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^(https?:\/\/)/i.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
          style={{ color: '#1e40af', textDecoration: 'underline', cursor: 'pointer' }}>
          Open link
        </a>
      );
    }
    if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(part)) {
      return (
        <a key={i} href={`mailto:${part}`}
          style={{ color: '#1e40af', textDecoration: 'underline', cursor: 'pointer' }}>
          Open link
        </a>
      );
    }
    return <span key={i}>{renderInline(part)}</span>;
  });
}

// Render inline markdown formatting: **bold** and *italic*.
function renderInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={i} style={{ color: PM.dark }}>{part.slice(2, -2)}</strong>;
    }
    if (/^\*[^*]+\*$/.test(part)) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{part}</span>;
  });
}

async function request(userQuery, threadId) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'POST',
    url: `${AI_BASE}/chat`,
    data: { user_query: userQuery, thread_id: threadId },
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    timeout: 600000,
  });
  return r.data;
}

async function fetchThreads() {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'GET',
    url: `${AI_BASE}/threads`,
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return r.data;
}

async function fetchThreadHistory(threadId) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'GET',
    url: `${AI_BASE}/thread_history`,
    params: { thread_id: threadId },
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return r.data;
}

async function deleteThread(threadId) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'DELETE',
    url: `${AI_BASE}/threads`,
    params: { thread_id: threadId },
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return r.data;
}

async function uploadFiles(threadId, fileList) {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('thread_id', threadId);
  for (const file of fileList) {
    formData.append('files', file);
  }
  const r = await axios({
    method: 'POST',
    url: `${AI_BASE}/upload_files`,
    data: formData,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    timeout: 600000,
  });
  return r.data;
}

function AssistantPopup({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  // `threadId` is the session id: the client generates one per conversation and
  // sends it in `thread_id` on every message so the server can thread the chat.
  const [threadId, setThreadId] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [threads, setThreads] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // thread pending deletion
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  const [consentLoaded, setConsentLoaded] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentScrolled, setConsentScrolled] = useState(false);
  const fileInputRef = useRef(null);
  const bottom = useRef(null);

  // Marks the legal text as fully read once the user scrolls to the bottom.
  const handleConsentScroll = (e) => {
    if (consentScrolled) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 8) {
      setConsentScrolled(true);
    }
  };

  useEffect(() => {
    if (open) setError('');
    // Start a fresh session each time the popup opens.
    if (open && !threadId) setThreadId(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));

    // Load the email consent from the backend the first time the popup opens and
    // prompt the user if they have never decided (null).
    if (open && !consentLoaded) {
      (async () => {
        try {
          const saved = await fetchEmailConsent();
          if (saved === true) {
            setEmailConsent(true);
          } else if (saved === false) {
            setEmailConsent(false);
          } else {
            setEmailConsent(false);
            setConsentOpen(true);
          }
        } catch {
          setEmailConsent(false);
        } finally {
          setConsentLoaded(true);
        }
      })();
    }
  }, [open, threadId, consentLoaded]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, busy]);

  const newChat = () => {
    setMessages([]);
    setThreadId(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
    setPrompt('');
    setAttachments([]);
  };

  const handleAttach = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAttachments((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const grantEmailConsent = async () => {
    setConsentSaving(true);
    try {
      await saveEmailConsent(true);
      setEmailConsent(true);
      setConsentOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not save email consent.');
    } finally {
      setConsentSaving(false);
    }
  };

  const declineEmailConsent = async () => {
    setConsentSaving(true);
    try {
      await saveEmailConsent(false);
      setEmailConsent(false);
      setConsentOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not save email consent.');
    } finally {
      setConsentSaving(false);
    }
  };

  const revokeEmailConsent = async () => {
    setConsentSaving(true);
    try {
      await saveEmailConsent(false);
      setEmailConsent(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not revoke email consent.');
    } finally {
      setConsentSaving(false);
    }
  };

  const openHistory = async (e) => {
    setHistoryAnchorEl(e.currentTarget);
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const list = await fetchThreads();
      setThreads(Array.isArray(list) ? list : []);
    } catch (err) {
      setThreads([]);
      setHistoryError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Could not load history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => setHistoryAnchorEl(null);

  const requestDelete = (e, t) => {
    e.stopPropagation();  // don't trigger selectThread
    setDeleteTarget(t);
  };

  const confirmDelete = async () => {
    const t = deleteTarget;
    if (!t) return;
    const id = t?.thread_id || t?.threadId || t?.id || t?.session_id;
    setDeleteTarget(null);   // close the dialog immediately
    if (!id) return;
    try {
      await deleteThread(id);
      // Remove from the local list (no need to re-open the menu).
      setThreads((prev) => prev.filter((x) => (x?.thread_id || x?.threadId || x?.id || x?.session_id) !== id));
      // If the deleted thread was the active one, start a new conversation.
      if (threadId === id) {
        setMessages([]);
        setThreadId(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
      }
    } catch (err) {
      setHistoryError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Could not delete conversation.');
    }
  };

  const selectThread = async (t) => {
    // Load the selected thread's messages and switch the active session to it.
    const id = t?.thread_id || t?.threadId || t?.id || t?.session_id;
    closeHistory();
    if (!id) return;

    setBusy(true);
    setError('');
    try {
      const result = await fetchThreadHistory(id);
      const msgs = Array.isArray(result?.messages) ? result.messages
        : (Array.isArray(result) ? result : []);
      const nextId = result?.thread_id || id;
      setThreadId(nextId);
      setMessages(msgs.map((m) => ({ role: m.role || 'user', content: m.content || '' })));
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Could not load conversation.');
      // Even if history fails, adopt the thread id so the user can continue it.
      setThreadId(id);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const text = prompt.trim();
    if ((!text && attachments.length === 0) || busy) return;
    setError('');
    setBusy(true);

    let pendingAttachments = attachments;
    setAttachments([]);
    setPrompt('');

    // Show the user's message (and any attached file names) immediately.
    const attachSummary = pendingAttachments.length
      ? '\n📎 ' + pendingAttachments.map((f) => f.name).join(', ')
      : '';
    setMessages((prev) => [...prev, { role: 'user', content: (text || '(attachments)') + attachSummary }]);

    try {
      // Upload any attached files to the ACTS AI workspace first.
      if (pendingAttachments.length) {
        setUploading(true);
        await uploadFiles(threadId, pendingAttachments);
        setUploading(false);
      }

      // Only call /chat when there is actual text; attachments alone just upload.
      if (text) {
        const result = await request(text, threadId);
        const answer = parseChatResponse(result);
        setMessages((prev) => [...prev, { role: 'assistant', content: answer || 'No answer returned.' }]);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Request failed.');
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, something went wrong.' }]);
    } finally {
      setUploading(false);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ style: { borderRadius: 16, height: '80vh', overflow: 'hidden' } }}>
      <Box display="flex" flexDirection="column" height="100%">
        {/* PM-style gradient header with ACTS logo */}
        <Box sx={{
          position: 'relative', overflow: 'hidden',
          background: PM.gradient, px: 2.5, py: 1.75,
          boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
        }}>
          <Box sx={{
            position: 'absolute', width: 160, height: 160, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.06)', top: -60, right: -30,
          }} />
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{ bgcolor: '#fff', width: 44, height: 44, boxShadow: '0 2px 8px rgba(0,0,0,0.2)', p: 0.5 }}
              variant="rounded"
            >
              <img src={actsLogo} alt="ACTS" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Avatar>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={900} color="#fff" lineHeight={1.2}>
                ACTS Assistant
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.85)">
                Private workspace · AI assistant
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title={emailConsent ? 'Email access on (read-only). Click to revoke.' : 'Allow AI read-only email access'}>
                <IconButton size="small" disabled={consentSaving}
                  onClick={() => { if (emailConsent) { revokeEmailConsent(); } else { setConsentChecked(false); setConsentScrolled(false); setConsentOpen(true); } }}
                  sx={{ color: emailConsent ? '#4ade80' : '#fff', bgcolor: emailConsent ? 'rgba(74,222,128,0.18)' : 'transparent' }}>
                  {consentSaving ? <CircularProgress size={18} color="inherit" /> : (emailConsent ? <MarkEmailReadIcon /> : <MailOutlineIcon />)}
                </IconButton>
              </Tooltip>
              <Tooltip title="Conversation history">
                <IconButton size="small" onClick={openHistory} sx={{ color: '#fff' }}><HistoryIcon /></IconButton>
              </Tooltip>
              <Tooltip title="New conversation">
                <IconButton size="small" onClick={newChat} sx={{ color: '#fff' }}><AddIcon /></IconButton>
              </Tooltip>
              <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
            </Box>
          </Box>
        </Box>

        <Menu anchorEl={historyAnchorEl} open={Boolean(historyAnchorEl)} onClose={closeHistory}
          PaperProps={{ style: { maxHeight: 340, minWidth: 320, borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.18)' } }}>
          <Box px={2} pt={1.4} pb={0.4}>
            <Typography variant="caption" color="text.secondary" fontWeight={800} letterSpacing={0.5}>
              CONVERSATION HISTORY
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          {historyLoading && (
            <Box px={2} py={2} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading history…</Typography>
            </Box>
          )}
          {!historyLoading && historyError && (
            <Box px={2} py={2}>
              <Typography variant="body2" color="#b91c1c">{historyError}</Typography>
            </Box>
          )}
          {!historyLoading && !historyError && threads.length === 0 && (
            <Box px={2} py={2}>
              <Typography variant="body2" color="text.secondary">
                No past conversations yet.
              </Typography>
            </Box>
          )}
          {!historyLoading && threads.map((t, i) => (
            <MenuItem key={t?.thread_id || t?.id || i} onClick={() => selectThread(t)} sx={{ py: 1 }}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <Typography variant="body2" noWrap color={PM.dark} fontWeight={600} style={{ flex: 1, minWidth: 0 }}>
                {t?.title || t?.name || `Conversation ${i + 1}`}
              </Typography>
              <IconButton size="small" onClick={(e) => requestDelete(e, t)} sx={{ color: PM.faint, ml: 1 }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))}
        </Menu>

        {error && (
          <Box px={2} py={1} sx={{ bgcolor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>{error}</Box>
        )}

        <Box flex={1} overflow="auto" px={2} py={2} bgcolor="#fff">
          {messages.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
              height="100%" textAlign="center" gap={1} color="text.secondary">
              <Avatar variant="rounded" sx={{ width: 80, height: 80, bgcolor: 'rgba(30,60,114,0.06)', p: 1.5, mb: 0.5 }}>
                <img src={actsLogo} alt="ACTS" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </Avatar>
              <Typography variant="h6" fontWeight={900} color={PM.dark}>
                Your expertise. A little more intelligence.
              </Typography>
              <Typography variant="body2" color={PM.muted}>
                Ask, analyze, and get work moving.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {messages.map((m, i) => (
                <Box key={i} display="flex" gap={1.5} alignItems="flex-start">
                  <Box width={34} height={34} borderRadius="50%" display="flex"
                    alignItems="center" justifyContent="center" flexShrink={0}
                    sx={{ bgcolor: m.role === 'user' ? '#e5e7eb' : '#fff', color: m.role === 'user' ? '#374151' : PM.primary, border: '1px solid #e2e8f0', p: m.role === 'assistant' ? 0.5 : 0 }}>
                    {m.role === 'user'
                      ? <PersonOutlineIcon fontSize="small" />
                      : <img src={actsLogo} alt="ACTS" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
                  </Box>
                  <Box flex={1}>
                    <Typography variant="caption" fontWeight={800} color={PM.muted}>
                      {m.role === 'user' ? 'You' : 'ACTS Assistant'}
                    </Typography>
                    <MessageContent content={m.content} />
                  </Box>
                </Box>
              ))}
              {busy && (
                <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                  <CircularProgress size={16} /> Thinking…
                </Box>
              )}
              <div ref={bottom} />
            </Box>
          )}
        </Box>

        <Box px={2} py={1.5} sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          {attachments.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
              {attachments.map((f, i) => (
                <Box key={i} display="flex" alignItems="center" gap={0.75}
                  sx={{ px: 1.25, py: 0.5, borderRadius: 8, bgcolor: 'rgba(30,60,114,0.08)', border: '1px solid', borderColor: PM.border }}>
                  <AttachFileIcon sx={{ fontSize: 15, color: PM.primary }} />
                  <Typography variant="caption" noWrap sx={{ maxWidth: 160, fontWeight: 700, color: PM.dark }}>
                    {f.name}
                  </Typography>
                  <IconButton size="small" onClick={() => removeAttachment(i)} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14, color: PM.muted }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          <Box display="flex" gap={1} alignItems="flex-end">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".doc,.docx,.pdf,.xls,.xlsx,.xlsm,.csv,.txt"
              style={{ display: 'none' }}
              onChange={handleAttach}
            />
            <IconButton onClick={() => fileInputRef.current?.click()} disabled={busy}
              aria-label="Attach a file"
              sx={{ bgcolor: '#eef2f7', color: PM.primary, '&:hover': { bgcolor: '#e2e8f0' }, '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#cbd5e1' } }}>
              <AttachFileIcon fontSize="small" />
            </IconButton>
            <TextField
              fullWidth multiline minRows={1} maxRows={4}
              variant="outlined" size="small"
              placeholder="Ask about your work…"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff' } }}
            />
            <IconButton onClick={send} disabled={(!prompt.trim() && attachments.length === 0) || busy}
              sx={{ bgcolor: PM.primary, color: '#fff', '&:hover': { bgcolor: PM.primaryDark }, '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}>
              {busy ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Email access consent dialog */}
      <Dialog open={consentOpen} onClose={declineEmailConsent} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MarkEmailReadIcon sx={{ color: PM.primary }} />
          Allow email access
        </DialogTitle>
        <DialogContent>
          <Box
            onScroll={handleConsentScroll}
            sx={{
              maxHeight: 380, overflowY: 'auto',
              bgcolor: '#f8fafc', border: '1px solid', borderColor: PM.border,
              borderRadius: 2, px: 2.5, py: 2, mb: 1.5,
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 4 },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Please review the following agreement before granting access.
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} color={PM.primary} sx={{ mb: 1, letterSpacing: 0.3, textTransform: 'uppercase' }}>
              Email Read-Only Access Agreement
            </Typography>
            <Typography variant="body2" color={PM.dark} sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
              {`This Email Read-Only Access Agreement ("Agreement") is entered into by and between you ("User") and ACTS Assistant ("the Service"). By selecting the checkbox below and clicking "Allow read-only access", you expressly acknowledge that you have read, understood, and agreed to be bound by all of the terms and conditions set forth herein.

              1. Grant of Limited Access. Subject to the terms of this Agreement, you grant the Service a limited, revocable, non-exclusive, non-transferable, and non-sublicensable right to access and read the contents of your email inbox, solely for the purpose of providing you with personalised assistance, summarising correspondence, and drafting responses on your behalf. You retain all right, title, and interest in and to your email content.

              2. Read-Only Limitation. The access granted hereunder is strictly read-only. The Service is expressly prohibited from, and you do not authorise the Service to, transmit, forward, delete, modify, archive, or otherwise alter any email, message, attachment, contact, calendar item, or other data within your mailbox in any manner whatsoever.

              3. Data Use and Confidentiality. Your email content will be used exclusively to fulfil your direct requests and to improve the accuracy of responses provided to you. The Service will not use your email content to train any machine-learning model, will not disclose your content to any third party, and will not share, sell, rent, or otherwise commercialise your information, except as required by applicable law.

              4. Security and Retention. The Service employs reasonable technical and organisational measures to protect your email content against unauthorised access, disclosure, alteration, and destruction. Any transient copies of your content created during processing are retained only for as long as is reasonably necessary to provide the Service and are thereafter deleted.

              5. Your Responsibilities. You are solely responsible for ensuring that you have the lawful right and authority to grant the access described herein, and that such access does not violate any duty of confidentiality, contractual obligation, or third-party right to which you are subject.

              6. Revocation. You may revoke the access granted under this Agreement at any time and for any reason, without penalty, by selecting the revoke option available in the Service header. Upon revocation, the Service shall promptly cease accessing your inbox.

              7. Disclaimer and Limitation of Liability. The Service is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied. To the fullest extent permitted by law, the Service shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or in connection with this Agreement.

              8. Governing Law. This Agreement shall be governed by and construed in accordance with the laws of the applicable jurisdiction, without regard to its conflict-of-law provisions.

              By scrolling to the end of this document, marking the acknowledgement checkbox, and selecting "Allow read-only access", you confirm your acceptance of the terms described above and authorise the Service to access your email inbox on a read-only basis.`}
            </Typography>
          </Box>
          {!consentScrolled ? (
            <Typography variant="caption" color={PM.muted} sx={{ display: 'block', mb: 1, textAlign: 'center', fontStyle: 'italic' }}>
              Scroll to the end of the agreement to accept.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} color="primary" />}
                label={<Typography variant="body2" color={PM.dark}>I have read and agree to the terms of this Email Read-Only Access Agreement.</Typography>}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={declineEmailConsent} disabled={consentSaving} color="inherit">Not now</Button>
          <Button onClick={grantEmailConsent} disabled={consentSaving || !consentChecked || !consentScrolled} variant="contained" autoFocus sx={{ bgcolor: PM.primary, '&:hover': { bgcolor: PM.primaryDark } }}>
            {consentSaving ? <CircularProgress size={18} color="inherit" /> : 'Allow read-only access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete conversation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete
            {' '}“{deleteTarget?.title || deleteTarget?.name || 'this conversation'}”.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default AssistantPopup;

